import { useEffect, useState, useRef } from 'react';

/**
 *
 * @param {RefObject} ref - Reference to the main DOM element being observed
 * @param {object} options - Hook configuration options
 * @param {function} options.onShowUp - Callback function executed when element comes into view
 * @param {function} options.onVanish - Callback function executed when element goes out of view
 * @param {RefObject} options.rootRef - Reference to the root element for intersection observer
 * @param {RefObject} options.bottomElRef - Reference to additional element to observe
 * @param {IntersectionObserverInit} options.settings - Additional intersection observer settings
 * @return {boolean} Current visibility state of the observed elements
 */
export function useOnScreen(
  ref,
  { onShowUp, onVanish, rootRef, bottomElRef = null, ...settings } = {}
) {
  const [isOnScreen, setIsOnScreen] = useState(false);
  const observerRef = useRef(null);
  const visibilityState = useRef({ main: false, bottom: false });

  useEffect(() => {
    // Function to update overall visibility state
    const updateVisibility = () => {
      const newIsOnScreen =
        visibilityState.current.main || visibilityState.current.bottom;
      setIsOnScreen((prev) => {
        if (prev !== newIsOnScreen) {
          // Execute callbacks only when state actually changes
          if (newIsOnScreen && onShowUp) {
            onShowUp(ref.current);
          } else if (!newIsOnScreen && onVanish) {
            onVanish(ref.current);
          }
        }
        return newIsOnScreen;
      });
    };

    // Create single observer for all elements
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === ref.current) {
            visibilityState.current.main = entry.isIntersecting;
          } else if (entry.target === bottomElRef?.current) {
            visibilityState.current.bottom = entry.isIntersecting;
          }
        });
        updateVisibility();
      },
      {
        root: rootRef?.current || null,
        ...settings,
      }
    );

    // Start observing elements
    if (ref.current) {
      observerRef.current.observe(ref.current);
    }

    if (bottomElRef?.current) {
      observerRef.current.observe(bottomElRef.current);
    }

    return () => {
      observerRef.current?.disconnect();
    };
  }, [ref.current, bottomElRef?.current, rootRef?.current, onShowUp, onVanish]);

  return isOnScreen;
}

export default useOnScreen;
